import { useMemo } from 'react';
import { bpsFormatter, formatDecimalAsPercentage, formatUSDCurrencyTwoDigits } from 'ns_libs/formatter';
import ColumnHeader from '../ColumnHeader';
import {
    BasicMetric, CostMetrics, InvestmentMetrics, ProjectionMetrics,
} from '../metrics';
import { IComparisonReportData, IDealSelection } from '../../../types';
import { ComparisonReportPropertyGroup, ComparisonReportRow } from '../constants';

export function useComparisonGridConfig(
    comparisonReportData: IComparisonReportData[] | undefined,
    selectedDeals: IDealSelection[],
    isEditMode: boolean,
    isLoading: boolean,
    removeSelectedDeal: (dealId: number) => void,
    updateSelectedProFormaId: (selectedDealId: number, selectedProFormaId: number) => void,
) {
    return useMemo(() => {
        if (!comparisonReportData || selectedDeals?.length === 0) return undefined;

        const config = {
            headerRowFormatter: (data: IComparisonReportData) => (
                <ColumnHeader
                    data={data}
                    key={`${data.dealId}-${data.proFormaId}`}
                    removeSelectedDeal={removeSelectedDeal}
                    updateSelectedProFormaId={updateSelectedProFormaId}
                    isEditMode={isEditMode}
                    isLoading={isLoading}
                />
            ),
            groups: [
                {
                    groupLabel: ComparisonReportPropertyGroup.PROPERTY_RETURNS,
                    rows: [
                        {
                            label: ComparisonReportRow.UNLEVERED,
                            formatter: (data: IComparisonReportData) => <InvestmentMetrics data={data.propertyReturns.unlevered} />,
                            isHighlighted: (data: IComparisonReportData) => data.propertyReturns.unlevered.isHighlighted || false,
                        },
                        {
                            label: ComparisonReportRow.LEVERED,
                            formatter: (data: IComparisonReportData) => <InvestmentMetrics data={data.propertyReturns.levered} />,
                            isHighlighted: (data: IComparisonReportData) => data.propertyReturns.levered.isHighlighted || false,
                        },
                    ],
                },
                {
                    groupLabel: ComparisonReportPropertyGroup.PARTNERSHIP_RETURNS,
                    rows: [
                        {
                            label: ComparisonReportRow.LIMITED_PARTNER,
                            formatter: (data: IComparisonReportData) => <InvestmentMetrics data={data.partnershipReturns.limitedPartner} />,
                            isHighlighted: (data: IComparisonReportData) => data.partnershipReturns.limitedPartner.isHighlighted || false,
                        },
                        {
                            label: ComparisonReportRow.GENERAL_PARTNER,
                            formatter: (data: IComparisonReportData) => <InvestmentMetrics data={data.partnershipReturns.generalPartner} />,
                            isHighlighted: (data: IComparisonReportData) => data.partnershipReturns.generalPartner.isHighlighted || false,
                        },
                    ],
                },
                {
                    groupLabel: ComparisonReportPropertyGroup.DEVELOPMENT_RETURNS,
                    rows: [
                        {
                            label: ComparisonReportRow.YIELD_ON_COST,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.developmentReturns.yieldOnCost}
                                    valueFormatter={(value: number) => formatDecimalAsPercentage(value, 2)}
                                />
                            ),
                            isHighlighted: (data: IComparisonReportData) => data.developmentReturns.yieldOnCost.isHighlighted || false,
                        },
                        {
                            label: ComparisonReportRow.MARKET_CAP_RATE,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.developmentReturns.marketCapRate}
                                    valueFormatter={(value: number) => formatDecimalAsPercentage(value, 2)}
                                />
                            ),
                            isHighlighted: (data: IComparisonReportData) => data.developmentReturns.marketCapRate.isHighlighted || false,
                        },
                        {
                            label: ComparisonReportRow.DEVELOPMENT_SPREAD,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.developmentReturns.developmentSpread}
                                    valueFormatter={bpsFormatter}
                                />
                            ),
                            isHighlighted: (data: IComparisonReportData) => data.developmentReturns.developmentSpread.isHighlighted || false,
                        },
                    ],
                },
                {
                    groupLabel: ComparisonReportPropertyGroup.DETAILS,
                    rows: [
                        { label: ComparisonReportRow.UNITS, formatter: (data: IComparisonReportData) => <BasicMetric value={data.details.units} /> },
                        {
                            label: ComparisonReportRow.NET_RENTABLE_SF,
                            formatter: (data: IComparisonReportData) => <BasicMetric value={data.details.netRentableSf} />,
                        },
                        {
                            label: ComparisonReportRow.LAND_AREA,
                            formatter: (data: IComparisonReportData) => <BasicMetric value={data.details.landArea} />,
                        },
                        {
                            label: ComparisonReportRow.BUILDINGS,
                            formatter: (data: IComparisonReportData) => <BasicMetric value={data.details.buildings} />,
                        },
                        { label: ComparisonReportRow.HEIGHT, formatter: (data: IComparisonReportData) => <BasicMetric value={data.details.height} /> },
                        {
                            label: ComparisonReportRow.PARKING_SPACES,
                            formatter: (data: IComparisonReportData) => <BasicMetric value={data.details.parkingSpaces} />,
                        },
                        {
                            label: ComparisonReportRow.PARKING_RATIO,
                            formatter: (data: IComparisonReportData) => <BasicMetric value={data.details.parkingRatio} />,
                        },
                    ],
                },
                {
                    groupLabel: ComparisonReportPropertyGroup.USES,
                    rows: [
                        {
                            label: ComparisonReportRow.ACQUISITION_COSTS,
                            formatter: (data: IComparisonReportData) => <CostMetrics data={data.uses.acquisitionCosts} />,
                        },
                        { label: ComparisonReportRow.HARD_COSTS, formatter: (data: IComparisonReportData) => <CostMetrics data={data.uses.hardCosts} /> },
                        { label: ComparisonReportRow.SOFT_COSTS, formatter: (data: IComparisonReportData) => <CostMetrics data={data.uses.softCosts} /> },
                        {
                            label: ComparisonReportRow.CARRY_COSTS,
                            formatter: (data: IComparisonReportData) => <CostMetrics data={data.uses.carryCosts} />,
                        },
                        {
                            label: ComparisonReportRow.TOTAL_USES,
                            formatter: (data: IComparisonReportData) => <CostMetrics data={data.uses.totalUses} />,
                            isHighlighted: (data: IComparisonReportData) => data.uses.totalUses.isHighlighted || false,
                        },
                    ],
                },
                {
                    groupLabel: ComparisonReportPropertyGroup.INCOME,
                    rows: [
                        {
                            label: ComparisonReportRow.EFFECTIVE_GROSS_REVENUE,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.effectiveGrossRevenue}
                                    valueFormatter={formatUSDCurrencyTwoDigits}
                                />
                            ),
                        },
                        {
                            label: ComparisonReportRow.OPERATING_EXPENSES,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.operatingExpenses}
                                    valueFormatter={formatUSDCurrencyTwoDigits}
                                />
                            ),
                        },
                        {
                            label: ComparisonReportRow.NET_OPERATING_INCOME,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.netOperatingIncome}
                                    valueFormatter={formatUSDCurrencyTwoDigits}
                                />
                            ),
                            isHighlighted: (data: IComparisonReportData) => data.income.netOperatingIncome.isHighlighted || false,
                        },
                        {
                            label: ComparisonReportRow.CAPITAL_EXPENDITURES,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.capitalExpenditures}
                                    valueFormatter={formatUSDCurrencyTwoDigits}
                                />
                            ),
                        },
                        {
                            label: ComparisonReportRow.CASH_FLOW_FROM_OPERATIONS,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.cashFlowFromOperations}
                                    valueFormatter={formatUSDCurrencyTwoDigits}
                                />
                            ),
                            isHighlighted: (data: IComparisonReportData) => data.income.cashFlowFromOperations.isHighlighted || false,
                        },
                        {
                            label: ComparisonReportRow.CAP_RATE,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.capRate}
                                    valueFormatter={(value: number) => formatDecimalAsPercentage(value, 2)}
                                />
                            ),
                        },
                        {
                            label: ComparisonReportRow.VALUATION,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.valuation}
                                    valueFormatter={formatUSDCurrencyTwoDigits}
                                />
                            ),
                        },
                        {
                            label: ComparisonReportRow.PERCENT_OF_TOTAL_PROJECT_COST,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.percentOfTotalProjectCost}
                                    valueFormatter={(value: number) => formatDecimalAsPercentage(value, 2)}
                                />
                            ),
                        },
                        {
                            label: ComparisonReportRow.VALUATION_PER_UNIT,
                            formatter: (data: IComparisonReportData) => (
                                <ProjectionMetrics
                                    data={data.income.valuationPerUnit}
                                    valueFormatter={formatUSDCurrencyTwoDigits}
                                />
                            ),
                        },
                    ],
                },
            ],
            data: comparisonReportData,
        };
        return config;
    }, [comparisonReportData,
        selectedDeals,
        isEditMode,
        isLoading,
        removeSelectedDeal,
        updateSelectedProFormaId]);
}
